"use client";

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { CHART_COLORS } from "@/lib/types";

interface CheckpointLineChartProps {
  data: Record<string, Record<string, number>>; // { ckpt: { lang: wer } }
  languages: string[];
  title: string;
  height?: number;
}

export default function CheckpointLineChart({ data, languages, title, height = 360 }: CheckpointLineChartProps) {
  const checkpoints = Object.keys(data).sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, "")) || 0;
    const numB = parseInt(b.replace(/\D/g, "")) || 0;
    return numA - numB;
  });

  const chartData = checkpoints.map((ckpt) => {
    const row: Record<string, string | number> = { checkpoint: ckpt };
    for (const lang of languages) {
      row[lang] = data[ckpt]?.[lang] ?? 0;
    }
    return row;
  });

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5">
      <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="checkpoint" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
          <Tooltip
            formatter={(value: unknown, name: unknown) => [`${Number(value).toFixed(2)}%`, String(name)]}
            contentStyle={{
              background: "#18181b",
              border: "1px solid #3f3f46",
              borderRadius: "8px",
              fontSize: "11px",
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: "10px", paddingTop: "8px" }}
            formatter={(value: string) => value.slice(0, 3).toUpperCase()}
          />
          {languages.map((lang, i) => (
            <Line
              key={lang}
              type="monotone"
              dataKey={lang}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 3, fill: CHART_COLORS[i % CHART_COLORS.length] }}
              activeDot={{ r: 5 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
